import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Volume2, VolumeX, Sparkles, Share2, Download } from 'lucide-react';
import type { Wrapped } from '@techsofit/api-types';
import { cn } from '@techsofit/ui';
import { StatOverlayPhoto } from './StatOverlayPhoto';
import { useCountUp } from '../lib/useCountUp';
import { valueLabel } from '../lib/goals';

const SLIDE_MS = 6000; // slow enough that each progress bar visibly fills (WhatsApp-style)
const OUTRO_MS = 8000; // the outro lingers a touch longer, then the story auto-closes

interface StoryProps {
  wrapped: Wrapped;
  onClose: () => void;
  onShare?: () => void;
  onSaveImage?: () => void;
}

/** Full-screen, auto-advancing Glow Up story (Spotify-Wrapped style) with music. */
export function GlowUpStory({ wrapped, onClose, onShare, onSaveImage }: StoryProps) {
  const slides = buildSlides(wrapped, onShare, onSaveImage);
  const [index, setIndex] = useState(0);
  const [muted, setMuted] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const next = () => setIndex((i) => Math.min(slides.length - 1, i + 1));
  const prev = () => setIndex((i) => Math.max(0, i - 1));

  const close = () => {
    audioRef.current?.pause();
    onCloseRef.current();
  };

  // Auto-advance; the final (outro) slide plays a beat longer, then auto-closes.
  useEffect(() => {
    const last = index >= slides.length - 1;
    const t = setTimeout(() => {
      if (last) {
        audioRef.current?.pause();
        onCloseRef.current();
      } else {
        setIndex((i) => i + 1);
      }
    }, last ? OUTRO_MS : SLIDE_MS);
    return () => clearTimeout(t);
  }, [index, slides.length]);

  // Music starts here — the story is mounted by a tap, so play() is allowed.
  useEffect(() => {
    const a = audioRef.current;
    if (a) {
      a.volume = 0.5;
      a.play().catch(() => undefined);
    }
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleMute() {
    const a = audioRef.current;
    if (a) {
      a.muted = !a.muted;
      setMuted(a.muted);
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-[60] bg-dark text-on-dark">
      <style>{'@keyframes glowbarGrow{from{transform:scaleX(0)}to{transform:scaleX(1)}}'}</style>
      <audio ref={audioRef} src="/glowup.mp3" loop preload="auto" />

      {/* Progress bars */}
      <div className="absolute inset-x-0 top-0 z-30 flex gap-1 p-2.5">
        {slides.map((s, i) => (
          <div key={s.key} className="h-1 flex-1 overflow-hidden rounded-pill bg-on-dark/25">
            {i < index && <div className="h-full w-full rounded-pill bg-on-dark" />}
            {i === index && (
              <div
                key={index}
                className="h-full w-full origin-left rounded-pill bg-on-dark"
                style={{ animation: `glowbarGrow ${index === slides.length - 1 ? OUTRO_MS : SLIDE_MS}ms linear forwards` }}
              />
            )}
          </div>
        ))}
      </div>

      {/* Top controls */}
      <div className="absolute right-2.5 top-6 z-30 flex items-center gap-1">
        <button onClick={toggleMute} aria-label={muted ? 'Unmute' : 'Mute'}
          className="inline-flex size-9 items-center justify-center rounded-control text-on-dark/80 hover:bg-on-dark/10">
          {muted ? <VolumeX className="size-5" aria-hidden /> : <Volume2 className="size-5" aria-hidden />}
        </button>
        <button onClick={close} aria-label="Close"
          className="inline-flex size-9 items-center justify-center rounded-control text-on-dark/80 hover:bg-on-dark/10">
          <X className="size-5" aria-hidden />
        </button>
      </div>

      {/* Tap zones (beneath the content layer) */}
      <button type="button" aria-label="Previous" onClick={prev} className="absolute inset-y-0 left-0 z-10 w-1/3" />
      <button type="button" aria-label="Next" onClick={next} className="absolute inset-y-0 right-0 z-10 w-2/3" />

      {/* Slide — pointer-events-none so taps fall through to the zones; buttons opt back in. */}
      <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center px-6">
        <div className="w-full max-w-sm">{slides[index].node}</div>
      </div>
    </div>,
    document.body,
  );
}

// ── Slides ───────────────────────────────────────────────────────────────────

function buildSlides(w: Wrapped, onShare?: () => void, onSaveImage?: () => void) {
  return [
    { key: 'intro', node: <IntroSlide w={w} /> },
    { key: 'headline', node: <HeadlineSlide w={w} /> },
    { key: 'stats', node: <StatsSlide w={w} /> },
    { key: 'photos', node: <PhotosSlide w={w} /> },
    ...(w.goals.length ? [{ key: 'goals', node: <GoalsSlide w={w} /> }] : []),
    { key: 'outro', node: <OutroSlide w={w} onShare={onShare} onSaveImage={onSaveImage} /> },
  ];
}

function IntroSlide({ w }: { w: Wrapped }) {
  const first = w.memberName.split(' ')[0] || w.memberName;
  return (
    <div className="animate-fade-up text-center">
      <Sparkles className="mx-auto size-9 text-accent" aria-hidden />
      <p className="mt-6 text-sm uppercase tracking-wide text-accent">{w.gymName}</p>
      <h1 className="mt-2 font-display text-4xl font-bold text-on-dark">{first}’s Glow Up</h1>
      {w.from && <p className="mt-3 text-sm text-on-dark-muted">{w.from} → {w.to}</p>}
    </div>
  );
}

function splitHeadline(h: string): { pre: string; num: number | null; post: string } {
  const m = h.match(/^(.*?)(\d[\d.]*)(.*)$/);
  if (!m) return { pre: h, num: null, post: '' };
  return { pre: m[1], num: parseFloat(m[2]), post: m[3] };
}

function HeadlineSlide({ w }: { w: Wrapped }) {
  const { pre, num, post } = splitHeadline(w.headline);
  const n = useCountUp(num ?? 0, num != null, 1100);
  const shown = num != null ? (Number.isInteger(num) ? Math.round(n) : n.toFixed(1)) : null;
  return (
    <div className="animate-fade-up text-center">
      <p className="text-sm uppercase tracking-wide text-accent">{w.gymName}</p>
      <p className="mt-8 font-display text-6xl font-bold leading-none text-accent tabular">
        {num != null ? `${pre}${shown}${post}` : w.headline}
      </p>
      {w.headlineSub && <p className="mt-4 text-on-dark-muted">{w.headlineSub}</p>}
    </div>
  );
}

function StatsSlide({ w }: { w: Wrapped }) {
  return (
    <div className="animate-fade-up">
      <p className="mb-5 text-center text-sm uppercase tracking-wide text-accent">By the numbers</p>
      <div className="grid grid-cols-3 gap-3 text-center">
        <CountStat value={w.totalCheckins} label={w.totalCheckins === 1 ? 'check-in' : 'check-ins'} />
        <CountStat value={w.goalsReached} label={w.goalsReached === 1 ? 'goal hit' : 'goals hit'} />
        <CountStat value={w.weeksTracked} label={w.weeksTracked === 1 ? 'week' : 'weeks'} />
      </div>
    </div>
  );
}

function CountStat({ value, label }: { value: number; label: string }) {
  const n = useCountUp(value, true, 900);
  return (
    <div className="rounded-card bg-on-dark/10 px-2 py-5">
      <p className="font-display text-3xl font-bold text-on-dark tabular">{Math.round(n)}</p>
      <p className="mt-1 text-xs text-on-dark-muted">{label}</p>
    </div>
  );
}

function PhotosSlide({ w }: { w: Wrapped }) {
  if (w.beforePhotoUrl && w.afterPhotoUrl) {
    const delta = w.afterValue != null && w.beforeValue != null ? w.afterValue - w.beforeValue : null;
    return (
      <div className="animate-fade-up">
        <p className="mb-3 text-center text-sm uppercase tracking-wide text-accent">The transformation</p>
        <div className="grid grid-cols-2 gap-2">
          <StatOverlayPhoto tall url={w.beforePhotoUrl} label="Before" value={w.beforeValue} unit={w.photoUnit ?? ''} />
          <StatOverlayPhoto tall url={w.afterPhotoUrl} label="After" value={w.afterValue} unit={w.photoUnit ?? ''} delta={delta} />
        </div>
      </div>
    );
  }
  const g = w.goals[0];
  return (
    <div className="animate-fade-up text-center">
      {g ? (
        <>
          <p className="text-sm uppercase tracking-wide text-accent">{g.title}</p>
          <p className="mt-8 font-display text-4xl font-bold text-on-dark tabular">
            {valueLabel(g.startValue, g.unit)} → {valueLabel(g.currentValue, g.unit)}
          </p>
        </>
      ) : (
        <p className="font-display text-3xl text-on-dark">Just getting started</p>
      )}
    </div>
  );
}

function GoalsSlide({ w }: { w: Wrapped }) {
  return (
    <div className="animate-fade-up">
      <p className="mb-4 text-center text-sm uppercase tracking-wide text-accent">Your goals</p>
      <div className="flex flex-col gap-2">
        {w.goals.slice(0, 5).map((g, i) => (
          <div key={i} className="flex items-center justify-between gap-3 rounded-card bg-on-dark/10 px-4 py-3">
            <span className="truncate text-on-dark">{g.title}</span>
            {g.reached ? (
              <span className="shrink-0 rounded-pill bg-accent px-2 py-0.5 text-xs font-semibold text-accent-ink">Reached</span>
            ) : g.delta != null && g.delta !== 0 ? (
              <span className={cn('shrink-0 text-sm font-medium tabular', g.improved ? 'text-accent' : 'text-on-dark-muted')}>
                {g.delta < 0 ? '−' : '+'}
                {valueLabel(Math.abs(g.delta), g.unit)}
              </span>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Inline so the stagger delays survive Tailwind's JIT (dynamic arbitrary values don't). */
const reveal = (delayMs: number) => ({ animationDelay: `${delayMs}ms`, animationFillMode: 'backwards' as const });

function OutroSlide({ w, onShare, onSaveImage }: { w: Wrapped; onShare?: () => void; onSaveImage?: () => void }) {
  return (
    <div className="text-center">
      <div className="animate-pop-in">
        <Sparkles className="mx-auto size-12 animate-pulse text-accent" aria-hidden />
      </div>
      <p className="mt-4 animate-fade-up font-display text-3xl font-bold text-on-dark" style={reveal(150)}>
        Keep glowing 💪
      </p>
      <p className="mt-3 animate-fade-up font-display text-xl font-semibold text-on-dark" style={reveal(350)}>
        {w.gymName}
      </p>
      <p className="animate-fade-up text-sm text-on-dark-muted" style={reveal(500)}>
        powered by TechSoFit
      </p>
      <div className="pointer-events-auto mx-auto mt-8 flex w-full max-w-xs animate-fade-up flex-col gap-2" style={reveal(700)}>
        {onShare && (
          <button
            onClick={(e) => { e.stopPropagation(); onShare(); }}
            className="inline-flex h-touch items-center justify-center gap-2 rounded-pill bg-accent px-5 font-medium text-accent-ink transition-transform active:scale-95"
          >
            <Share2 className="size-4" aria-hidden /> Share
          </button>
        )}
        {onSaveImage && (
          <button
            onClick={(e) => { e.stopPropagation(); onSaveImage(); }}
            className="inline-flex h-touch items-center justify-center gap-2 rounded-pill border border-on-dark/30 px-5 font-medium text-on-dark transition-transform active:scale-95"
          >
            <Download className="size-4" aria-hidden /> Save image
          </button>
        )}
      </div>
      <p className="mt-8 animate-fade-up text-xs text-on-dark/55" style={reveal(950)}>
        A Techsolutely Innovations product
      </p>
    </div>
  );
}
